import PageMeta from '@/components/base/PageMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/movies';
import { useRef, useState } from 'react';

const siteUrl = 'https://pipicinema.com';
const contactFormUrl = 'https://readdy.ai/api/form/d9sqt4p4ne17ei0sdmd0';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于屁屁影院 - 品牌介绍',
  description: '了解屁屁影院的品牌故事、发展历程、核心优势与联系方式。屁屁影院致力于为广大用户提供海量高清影视资源，打造优质的在线观影平台。',
  url: `${siteUrl}/about`,
  isPartOf: {
    '@type': 'WebSite',
    name: '屁屁影院',
    url: siteUrl,
  },
};

export default function AboutPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormMsg('感谢您的留言，我们会尽快回复！');
      return;
    }

    setFormStatus('loading');
    setFormMsg('');

    const formData = new FormData(form);
    if (honeypotInput) {
      formData.delete('website_alt');
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const resp = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const text = await resp.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (resp.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        setFormMsg('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message || '';
        setFormStatus('error');
        setFormMsg(serverMsg.includes('spam') ? '提交失败，请稍后再试。' : (serverMsg || '提交失败，请稍后再试或直接发送邮件联系我们。'));
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 屁屁影院品牌介绍与联系方式"
        description="了解屁屁影院的品牌故事与核心优势。屁屁影院汇聚海量高清电影、电视剧、动漫及综艺节目，每日更新，多终端支持，免费畅享优质观影体验。获取联系方式与常见问题解答。"
        keywords="屁屁影院品牌,屁屁影院介绍,屁屁影院联系方式,关于屁屁影院,在线观影平台"
        canonicalUrl={`${siteUrl}/about`}
        schema={aboutSchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative h-[300px] md:h-[380px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20office%20team%20collaboration%20scene%20with%20warm%20lighting%2C%20film%20posters%20on%20wall%2C%20creative%20workspace%2C%20cinema%20related%20decorations%2C%20friendly%20atmosphere%2C%20golden%20and%20warm%20brown%20tones%2C%20clean%20professional%20style&width=1800&height=800&seq=pipi-about-hero&orientation=landscape"
            alt="屁屁影院团队"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background-950/60"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-3">
              关于屁屁影院
            </h1>
            <p className="text-sm md:text-base text-background-300 max-w-xl mx-auto">
              致力于打造最优质的在线观影平台
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <article>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
                品牌介绍
              </h2>
              <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                <p>
                  <strong className="text-foreground-950">屁屁影院</strong>成立于2020年，是一家专注于在线影视内容分发的互联网平台。自创立以来，我们始终秉持"让每个人都能轻松享受优质影视内容"的理念，致力于为中文用户打造一个资源丰富、体验流畅的在线观影平台。
                </p>
                <p>
                  平台汇聚了<strong className="text-foreground-950">超过10,000部</strong>高清影视资源，涵盖电影、电视剧、动漫、综艺四大品类。无论是好莱坞大片、国产精品、日韩剧集还是热门番剧，在屁屁影院都能找到。我们拥有一支专业的内容采集团队，确保影片资源<strong className="text-foreground-950">每日更新</strong>，让用户不错过任何精彩内容。
                </p>
                <p>
                  技术层面，屁屁影院采用先进的视频编码与CDN加速技术，支持<strong className="text-foreground-950">720P至1080P高清画质</strong>的流畅播放。同时，平台采用<strong className="text-foreground-950">响应式设计</strong>，完美适配电脑、平板、手机等多种终端设备，让用户随时随地享受观影乐趣。
                </p>
                <p>
                  最值得一提的是，屁屁影院始终坚持<strong className="text-foreground-950">完全免费</strong>的服务模式，无需注册、无需付费即可畅享所有内容。我们相信，优质的影视内容应该被更多人看到，这也是屁屁影院最核心的价值所在。
                </p>
              </div>
            </article>

            {/* Core Values */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
              {[
                { icon: 'ri-movie-line', title: '海量资源', desc: '超过10,000部高清影片，四大品类全覆盖' },
                { icon: 'ri-speed-up-line', title: '极速更新', desc: '每日同步最新资源，热门内容即时上线' },
                { icon: 'ri-device-line', title: '多端适配', desc: '电脑、平板、手机无缝切换，随时随地观看' },
                { icon: 'ri-shield-check-line', title: '完全免费', desc: '无需注册、无需付费，打开即看' },
              ].map((item, idx) => (
                <div key={idx} className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center text-primary-500">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1.5">{item.title}</h3>
                  <p className="text-xs text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2 text-center">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              来自真实用户的使用评价
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-xs ${i < t.rating ? 'text-primary-500 ri-star-fill' : 'text-foreground-300 ri-star-line'}`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或建议？欢迎随时与我们联系
            </p>

            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
              <form
                ref={formRef}
                onSubmit={handleContactSubmit}
                action={contactFormUrl}
                method="POST"
                data-readdy-form=""
                className="space-y-4"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : (
                    '发送留言'
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="text-center py-2">
                    <p className="text-sm text-accent-600 font-medium flex items-center justify-center gap-1.5">
                      <i className="ri-checkbox-circle-line"></i>
                      {formMsg}
                    </p>
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="text-center py-2">
                    <p className="text-sm text-red-600 font-medium">{formMsg}</p>
                  </div>
                )}
              </form>

              <div className="mt-8 pt-6 border-t border-background-200/70 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="text-sm font-medium text-foreground-950">contact@pipicinema.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">在线客服</p>
                    <p className="text-sm font-medium text-foreground-950">每日 9:00 - 24:00</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">办公地址</p>
                    <p className="text-sm font-medium text-foreground-950">中国·北京</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}